import { useCallback, useMemo } from 'react';

import { isDefined, isNotDefined, isNotEmpty, isOfType } from '@hsp/utilities';

import { useIsExisting } from 'pages/audience/shared/save/hooks';
import { useSaveStoreSelector } from 'pages/audience/shared/save/hooks/context';
import { IAudience, IAudienceShop } from 'types/api/audience';
import { s } from 'utilities/strings';

export const useGetDisplayName = () => {
  const data = useSaveStoreSelector(({ data }) => data);
  const formType = useSaveStoreSelector(({ formType }) => formType);
  const saveAsMaster = useSaveStoreSelector(({ saveAsMaster }) => saveAsMaster);
  const saveAsPreBuilt = useSaveStoreSelector(({ saveAsPreBuilt }) => saveAsPreBuilt);
  const saveTo = useSaveStoreSelector(({ saveTo }) => saveTo);

  const isExisting = useIsExisting();

  const newAudience = useMemo(() => s(['Site.New', 'Site.Audience']), []);
  const newMasterFile = useMemo(() => s(['Site.New', 'Site.MasterFile']), []);
  const newPrebuilt = useMemo(() => s(['Site.New', 'Site.Prebuilt']), []);
  const newShop = useMemo(() => s(['Site.New', 'Site.Shop']), []);

  const shopName = useCallback(
    (shop: IAudienceShop) => {
      if (isNotDefined(shop)) {
        return newAudience;
      }

      if (saveTo) {
        return newShop;
      }

      if (saveAsMaster) {
        return newMasterFile;
      }

      if (saveAsPreBuilt) {
        return newPrebuilt;
      }

      const { name, mailCode } = shop;
      let displayName = name;

      if (isDefined(mailCode) && isNotEmpty(mailCode)) {
        displayName += ` (${mailCode})`;
      }

      return displayName ?? newAudience;
    },
    [newAudience, newMasterFile, newPrebuilt, newShop, saveAsMaster, saveAsPreBuilt, saveTo]
  );

  return useCallback(() => {
    if (formType === 'basic' || formType === 'master-file' || formType === 'pre-built') {
      return shopName(data as IAudienceShop);
    }

    if (saveAsMaster) {
      return newMasterFile;
    }

    if (saveAsPreBuilt) {
      return newPrebuilt;
    }

    if (isOfType<IAudience>(data, 'category')) {
      if (isExisting) {
        return data.name;
      }

      return saveTo ? newShop : newAudience;
    }

    return newShop;
  }, [
    data,
    formType,
    isExisting,
    newAudience,
    newMasterFile,
    newPrebuilt,
    newShop,
    saveAsMaster,
    saveAsPreBuilt,
    saveTo,
    shopName,
  ]);
};
